/**

Coded by www.techhighway.ca

*/

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";

// Material Dashboard 2 React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";

import DataTable from "components/Tables/DataTable";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

// Data
import { keyframes, Typography } from "@mui/material";
import MDCheckbox from "components/MDCheckbox";
import MDCircularProgress from "components/MDCircularProgress";
import MDCircularProgressRoot from "components/MDCircularProgress/MDCircularProgressRoot";
import MDTypography from "components/MDTypography";
import auctionData from "layouts/active-listings/data";
import { useRef, useState } from "react";
import wildlifeAuctionService from "services/wildlife-auction-service";
import { sourceOptions } from "utility/constant-dropdown";
import { generateAndDownloadExcelFile } from "utility/excelExport";
import { formatRemainingTime, getStatus } from "utility/helpers";
// logo
import logo from "assets/images/logo.png";
import ExportDropdownPanel from "components/ListingComponents/exportDropdownPanel";
import GenderFormInputs from "components/ListingComponents/genderFormInput";
import SummaryCard from "components/ListingComponents/summaryCard";
import { generateAnimalPDF } from "utility/generateAnimalPDF";

dayjs.extend(advancedFormat);
const columnDetails = [
  { col: 'id', label: 'Id', type: '', color: '', width: '10' },
  { col: 'listing_id', label: 'Listing Id', type: 'link', color: '', width: '12', linkCol: 'listing_url' },
  { col: 'price', label: 'Final Price', type: 'decimal', color: '', width: '20' },
  { col: 'title', label: 'Title', type: '', color: '', width: '50' },
  { col: 'subtitle', label: 'Subtitle', type: '', color: '', width: '50' },
  { col: 'source', label: 'Source', type: '', color: '', width: '20' },
  { col: 'status', label: 'Status', type: '', color: '', width: '20' },
  { col: 'click_here', label: 'Image', type: 'link', color: '', width: '10', linkCol: 'image_url' },
  { col: 'seller', label: 'Seller', type: '', color: '', width: '35' },
  { col: 'listing_type', label: 'Type', type: '', color: '', width: '15' },
  { col: 'avg_price', label: 'Avg. Price', type: 'decimal', color: '', width: '20' },
  { col: 'total_male_price', label: 'Total Male Price', type: 'decimal', color: '', width: '20' },
  { col: 'total_female_price', label: 'Total Female Price', type: 'decimal', color: '', width: '20' },
  { col: 'male', label: 'Male', type: 'number', color: '', width: '12' },
  { col: 'female', label: 'Female', type: 'number', color: '', width: '12' },
  { col: 'male_calf', label: 'Male Calf', type: 'number', color: '', width: '12' },
  { col: 'female_calf', label: 'Female Calf', type: 'number', color: '', width: '12' },
  { col: 'unsexed_calf', label: 'Unsexed Calf', type: 'number', color: '', width: '12' },
  { col: 'category', label: 'Category', type: '', color: '', width: '12' },
  { col: 'species', label: 'Species', type: '', color: '', width: '30' },
  { col: 'description', label: 'Description', type: '', color: '', width: '60' },
  { col: 'reserve_status', label: 'Reserve Status', type: '', color: '', width: '20' },
  { col: 'remaining_time', label: 'Remaining Time', type: '', color: '', width: '20' },
  { col: 'start_date', label: 'Start Date', type: '', color: '', width: '20' },
  { col: 'end_date', label: 'End Date', type: '', color: '', width: '20' },
  { col: 'qty', label: 'Qty', type: '', color: '', width: '10' },
];

//css const for scraping icon to show blink effect
const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

function Status() {
  const {
    columns,
    rows,
    open,
    handleClickOpen,
    handleClose,
    deleteWildlifeAuction,
    selectedId,
    totalData,
    page,
    pageSize,
    getData,
    sortColumn,
    sortColumnValue,
    searchValue,
    pagePermission,
    show_filter,
    filterOn,
    filterOff,
    isData,
    openAlertSB,
    closeAlertSB,
    excelRows,
    openGender,
    formData,
    setFormData,
    handleSave,
    scrapingStatus,
    summaryData,
  } = auctionData();

  const [excelLoading, setExcelLoading] = useState(false);
  const [scrapeDialogueOpen, setScrapeDialogueOpen] = useState(false);
  const [scrapeSource, setScrapeSource] = useState(() =>
    sourceOptions.reduce((acc, opt) => {
      if (!opt.disabled) {
        acc[opt.key] = false;
      }
      return acc;
    }, {})
  );
  const [scrapeError, setScrapeError] = useState(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("excel");
  const buttonRef = useRef(null);

  const handleGenerate = async () => {
    setExcelLoading(true);

    try {
      //1 Fetch full data only once
      let data = await wildlifeAuctionService.getWildlifeAuctions(
        null, null, null, null, null, 1
      );

      // 2 Filter by selected excelRows (if any)
      data = data.filter(
        (row) => excelRows.length === 0 || excelRows.includes(row.id)
      );

      // 3 Calculate summary variables
      let totalAnimalCount = 0;
      let totalPrice = 0;
      let totalMale = 0;
      let totalMalePrice = 0;
      let totalFemale = 0;
      let totalFemalePrice = 0;
      let totalMaleCalf = 0;
      let totalMaleCalfPrice = 0;
      let totalFemaleCalf = 0;
      let totalFemaleCalfPrice = 0;
      let totalUnsexedCalf = 0;
      let totalUnsexedCalfPrice = 0;

      data.forEach((row) => {
        const final_price = Number(row.price);
        const final_count = Number(row.male) + Number(row.female) + Number(row.male_calf) + Number(row.female_calf) + Number(row.unsexed_calf);
        const avg_price = final_count > 0 ? final_price / final_count : 0;

        totalMale += Number(row.male);
        totalFemale += Number(row.female);
        totalMaleCalf += Number(row.male_calf);
        totalFemaleCalf += Number(row.female_calf);
        totalUnsexedCalf += Number(row.unsexed_calf);

        totalPrice += final_price;

        totalMalePrice += Number(row.male) * avg_price;
        totalFemalePrice += Number(row.female) * avg_price;
        totalMaleCalfPrice += Number(row.male_calf) * avg_price;
        totalFemaleCalfPrice += Number(row.female_calf) * avg_price;
        totalUnsexedCalfPrice += Number(row.unsexed_calf) * avg_price;
      });

      totalAnimalCount = totalMale + totalFemale + totalMaleCalf + totalFemaleCalf + totalUnsexedCalf;

      // 4 Prepare summaryConfig for Excel
      const summaryConfig = [
        {
          type: "title",
          value: "Summary",
          mergeAcross: true,
          style: {
            font: { size: 14, bold: true, color: "FFFFFF" },
            fill: { color: "057DDE" },
            alignment: { horizontal: "left", vertical: "middle" },
          },
        },
        {
          type: "row",
          label: "Total Animals :",
          value: totalAnimalCount,
          dataType: "number",
          style: { label: { bold: true }, value: { alignment: "right", fill: "#FFFFFF" } },
        },
        // {
        //   type: "row",
        //   label: "Final Total Price :",
        //   value: totalPrice,
        //   dataType: "decimal",
        //   style: { label: { bold: true }, value: { alignment: "right", fill: "#FFFFFF" } },
        // },
        // {
        //   type: "row",
        //   label: "Avg. Animal Price :",
        //   dataType: "decimal",
        //   value: totalAnimalCount > 0 ? totalPrice / totalAnimalCount : 0,
        //   style: { label: { bold: true }, value: { alignment: "right", fill: "#FFFFFF" } },
        // },
        {
          type: "table",
          headers: ["Gender", "Count", "Total Price", "Avg. Price Each"],
          data: [
            ["Male", totalMale, totalMalePrice, totalMale > 0 ? totalMalePrice / totalMale : 0],
            ["Female", totalFemale, totalFemalePrice, totalFemale > 0 ? totalFemalePrice / totalFemale : 0],
            ["Male Calf", totalMaleCalf, totalMaleCalfPrice, totalMaleCalf > 0 ? totalMaleCalfPrice / totalMaleCalf : 0],
            ["Female Calf", totalFemaleCalf, totalFemaleCalfPrice, totalFemaleCalf > 0 ? totalFemaleCalfPrice / totalFemaleCalf : 0],
            ["Unsexed Calf", totalUnsexedCalf, totalUnsexedCalfPrice, totalUnsexedCalf > 0 ? totalUnsexedCalfPrice / totalUnsexedCalf : 0],
          ].filter((row) => row[1] > 0),
          columnTypes: ["string", "number", "decimal", "decimal"],
          columnStyles: [
            { alignment: "left" },
            { alignment: "center" },
            { alignment: "right" },
            { alignment: "right" },
          ],
        },
      ];
      // 5 Map data for Excel
      let excelData = [];
      if (selectedOption === "excel" || selectedOption === "both") {
        excelData = data.map((row) => ({
          ...row,
          remaining_time: formatRemainingTime(row.end_date),
          status: getStatus(row.end_date, row.reserve_status),
          click_here: "Click Here",
        }));
      }

      // 6 Map data for PDF
      let pdfData = [];
      if (selectedOption === "pdf" || selectedOption === "both") {
        pdfData = await Promise.all(
          data.map(async (row) => ({
            photo: row.image_url ? await wildlifeAuctionService.getBase64Image(row.image_url, row.id) : "",
            id: row.id || "",
            source: row.source || "",
            listingId: row.listing_id || "",
            link: row.listing_url || "",
            title: row.title || row.name || "",
            description: row.description || "",
            finalPrice: row.price ? `$${row.price}` : "-",
            closeDate: row.end_date,
            status: getStatus(row.end_date, row.reserve_status),
            qty: row.qty
          }))
        );
      }

      // 7 Generate files
      if (selectedOption === "excel") {
        await generateAndDownloadExcelFile(excelData, columnDetails, "Active Listings", summaryConfig, 4);
      } else if (selectedOption === "pdf") {
        await generateAnimalPDF(pdfData, logo, summaryConfig, "Price Comparison Data - Active Listing");
      } else if (selectedOption === "both") {
        await generateAndDownloadExcelFile(excelData, columnDetails, "Active Listings", summaryConfig, 4);
        await generateAnimalPDF(pdfData, logo, summaryConfig, "Price Comparison Data - Active Listing");
      }

    } catch (error) {
      console.error("Error generating files:", error);
    } finally {
      setDropdownOpen(false);
      setExcelLoading(false);
    }
  };

  const handleSourceChange = (key, checked) => {
    setScrapeSource((prev) => ({
      ...prev,
      [key]: checked
    }));
    setScrapeError(null);
  };

  //Function to trigger scrape manually
  const UpdateScraperList = async () => {
    const hasAnySelected = Object.values(scrapeSource).some((val) => val === true);
    if (hasAnySelected) {
      setScrapeError(null);
      const data = {
        sources: Object.keys(scrapeSource).filter((key) => scrapeSource[key] === true)
      };
      let response = await wildlifeAuctionService.startScraping(data);
      if (response) {
        openAlertSB({ color: "success", icon: "check", msg: response, title: "Manual Scraping" });
        setTimeout(function () {
          closeAlertSB();
          window.location.reload();
        }, 4000);
      }
      setScrapeDialogueOpen(false);
      setScrapeError(null);
    } else {
      setScrapeError('Please check at least one source');
    }
  }

  //Function to handle open start scraping dailogue box where status is also fetched
  const handleScraperDailogueOpen = async () => {
    const anyInProgress = Object.values(scrapingStatus || {}).some(
      (status) => status?.in_progress
    );
    setScrapingInProgress(anyInProgress);
    setScrapeDialogueOpen(true);
  }

  return (
    <>
      <DashboardLayout breadcrumbsTitle="Active Listings">
        <MDBox>
          <DataTable
            entriesPerPage={{
              defaultValue: pageSize,
            }}
            canSearch
            canFilter={true}
            showTotalEntries
            table={{ columns, rows }}
            isSorted
            totalData={totalData}
            initialPageIndex={page - 1}
            getData={getData}
            pageCount={Math.ceil(totalData / pageSize)}
            sortCol={sortColumn}
            sortColValue={sortColumnValue}
            searchVal={searchValue}
            noEndBorder={false}
            isData={isData}
            showFilter={show_filter}
            filterOn={filterOn}
            filterOff={filterOff}
          />
          {/* Divider between table and summary */}
          <MDBox my={1} sx={{ borderBottom: "1px solid #e0e0e0" }} />
          {/* Summary Section — aligned column */}
          <SummaryCard summaryData={summaryData} />
        </MDBox>

        {/* Action buttons for listing pages should go inside this MDBox */}
        <MDBox position="absolute" right="10px" mt={-4.5} display="flex" gap={1}>
          <MDBox sx={{ position: "relative", display: "inline-block" }}>
            {/* Trigger Export Button */}
            <MDButton
              ref={buttonRef}
              alignItems="center"
              variant="gradient"
              color="warning"
              fullWidth
              title="Export"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              Export
              {excelLoading ? (
                <MDCircularProgress size={20} />
              ) : (
                <Icon sx={{ fontWeight: "bold", ml: 1 }}>download</Icon>
              )}
            </MDButton>

            {/* Dropdown Panel */}
            {dropdownOpen && (
              <ExportDropdownPanel
                selectedOption={selectedOption}
                onOptionChange={setSelectedOption}
                onGenerate={handleGenerate}
                onClickAway={() => setDropdownOpen(false)}
              />
            )}
          </MDBox>
          <MDButton
            variant="gradient"
            color="success"
            fullWidth
            title={'Start Scraping'}
            onClick={(e) => handleScraperDailogueOpen()}
            iconOnly
            circular
            sx={{
              animation: scrapingInProgress ? `${blink} 1.2s infinite ease-in-out` : "none",
            }}
          >
            <Icon sx={{ fontWeight: "bold" }}>travel_explore_icon</Icon>
          </MDButton>
          {pagePermission.new ? (
            <MDButton
              component={Link}
              color="info"
              coloredShadow="info"
              variant="gradient"
              to="/active-listings/add"
              title="Add New Auction"
              iconOnly
              circular
            >
              <Icon fontSize="medium" sx={{ fontWeight: "bold", fontSize: "16px" }}>
                add
              </Icon>
            </MDButton>
          ) : (
            ""
          )}
        </MDBox>
      </DashboardLayout >

      <MDDialog
        open={open}
        onClose={handleClose}
        successAction={() => deleteWildlifeAuction(selectedId)}
        dialogTitle="Are you Sure?"
        cancleButton="No, Please!"
        successButton="Yes, Delete it!"
        cancleButtonColor="light"
        successButtonColor="info"
        icon={
          <Icon sx={{ width: "100%", marginTop: "25px" }} fontSize="large" color="primary">
            error
          </Icon>
        }
      >
        <p>You will not be able to recover the deleted record!</p>
      </MDDialog>
      <MDDialog
        open={scrapeDialogueOpen}
        onClose={() => {
          setScrapeDialogueOpen(false);
          setScrapeError(null);
          setScrapingInProgress(false);//commented just to see the effect for long time other wise it should be un comment
        }}
        successAction={UpdateScraperList}
        cancleButton="Cancel"
        successButton="Start"
        cancleButtonColor="light"
        successButtonColor="success"
        dialogTitle="Start Scraping - Select Source"
      >
        <MDBox display="flex" flexDirection="column" gap={1} mt={1}>
          {scrapeError ? (<Typography variant="body2" color={'error'}>{scrapeError}</Typography>) : ''}
          {sourceOptions.filter((option) => !option.disabled).map((option) => {
            const status = scrapingStatus?.[option.key] || {};
            const { in_progress: inProgress, last_parsed: lastParsed } = status;

            const label = (
              <MDTypography
                sx={{
                  color: "#7b809a",
                  fontSize: "0.775rem",
                  fontWeight: "700",
                }}
                display="block"
                variant="body2"
              >
                {option.value}
              </MDTypography>
            );

            return (
              <MDBox
                key={option.value}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                mb={1}
              >
                <MDBox display="flex" alignItems="center">
                  {inProgress ? (
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                      <MDCircularProgressRoot size={15} title="Scraping is in progress" color="success" sx={{ py: 0, mr: 0 }} />
                      {label}
                    </MDBox>
                  ) : (
                    <MDCheckbox
                      label={label}
                      checked={scrapeSource[option.key]}
                      onChange={(e) =>
                        handleSourceChange(option.key, e.target.checked)
                      }
                      sx={{ py: 0, mr: 0 }}
                    />
                  )}
                </MDBox>

                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", color: "#220bcfff" }}
                >
                  Last Scraped :{" "}
                  {lastParsed
                    ? dayjs(lastParsed).format("Do MMM, YYYY hh:mm A")
                    : "N/A"}
                </Typography>
              </MDBox>
            );
          })}
        </MDBox>
      </MDDialog>
      <MDDialog
        open={openGender}
        onClose={handleClose}
        successAction={() => handleSave(formData)}
        cancleButton="Close"
        successButton="Save"
        cancleButtonColor="primary"
        successButtonColor="info"
        dialogTitle="Edit Gender Details"
      >
        <GenderFormInputs
          formData={formData}
          onFormDataChange={setFormData}
        />
      </MDDialog>
    </>
  );
}

export default Status;
